import { DeviceType } from 'editor/src/store/app/types';
import { updateSidebarTabsAction } from 'editor/src/store/editorModules/sidebar/slice';
import { isNotDefaultTab, SystemTabNames, TabInfo } from 'editor/src/store/editorModules/sidebar/types';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import getCustomerDeviceType from 'editor/src/util/getCustomerDeviceType';

const tabId = (tab: TabInfo) => (isNotDefaultTab(tab) ? tab.id : tab);

/**
 * @param keepActiveTab keep the open tab open even though the number of tabs changes (used for
 * tabs a host switches on or off at any time, so the customer's sidebar does not jump).
 */
const updateSidebarTabsOperation =
  (keepActiveTab = false) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    const { baseTabs, pluginTabs, requestedBaseTabs, dynamicTabs } = state.editorModules.sidebar;
    const newTabs: TabInfo[] = [...baseTabs];

    // positional tabs first, so their index does not depend on anchored tabs toggled before them
    const anchoredTabs = dynamicTabs.filter((dynamicTab) => dynamicTab.after);
    dynamicTabs.forEach((dynamicTab) => {
      if (dynamicTab.after) {
        return;
      }
      if (newTabs.some((tab) => tabId(tab) === dynamicTab.tab)) {
        return; // the host already lists this tab
      }

      if (dynamicTab.position === undefined) {
        newTabs.push(dynamicTab.tab);
        return;
      }

      newTabs.splice(dynamicTab.position, 0, dynamicTab.tab);
    });

    anchoredTabs.forEach((dynamicTab) => {
      if (newTabs.some((tab) => tabId(tab) === dynamicTab.tab)) {
        return; // the host already lists this tab
      }
      const afterIndex = newTabs.findIndex((tab) => tabId(tab) === dynamicTab.after);
      if (afterIndex !== -1) {
        newTabs.splice(afterIndex + 1, 0, dynamicTab.tab);
      } else if (dynamicTab.position === undefined) {
        newTabs.push(dynamicTab.tab);
      } else {
        newTabs.splice(dynamicTab.position, 0, dynamicTab.tab);
      }
    });

    // add plugin tab to specific index in the tab list if specified
    pluginTabs.forEach((pluginTab) => {
      const plugin = state.plugins.list.find((plugin) => plugin.name === pluginTab.plugin);
      if (plugin?.indexInTabs !== undefined) {
        newTabs.splice(plugin.indexInTabs, 0, pluginTab);
        return;
      }

      const baseTabPosition = requestedBaseTabs.findIndex((tab) => tab === pluginTab.id);
      if (baseTabPosition !== -1) {
        newTabs.splice(baseTabPosition, 0, pluginTab);
        return;
      }

      newTabs.push(pluginTab);
    });

    dispatch(
      updateSidebarTabsAction({
        tabs: [...newTabs, ...SystemTabNames],
        isMobile: getCustomerDeviceType() !== DeviceType.Desktop,
        ...(keepActiveTab ? { keepActiveTab } : {}),
      }),
    );
  };

export default updateSidebarTabsOperation;
